/**
 * One parse, one write for the SDMS open chain (speed, 2026-09-30).
 *
 * The open chain (merge → summaries → dropdowns → calibration → raw data → saved values → dilution)
 * used to parse the whole workbook with ExcelJS and write it back out at EVERY step — on a VOC batch
 * that is ~450 ms per parse and ~700 ms per write, ~13 s in total before the sheet appears. Each step
 * now also has a `…ToWorkbook` form that edits a workbook already in memory, so the page parses once
 * (the merge) and writes once (`writeSdmsWorkbook`) at the end. The buffer forms stay as thin
 * wrappers (load → the same edit → write), so every other caller and test is unchanged.
 */
import type { Workbook } from 'exceljs';
import { repairCfTextAttrs } from './sdmsCfTextRepair';

export type SdmsWorkbook = Workbook;

/** Parse `buf` into a fresh ExcelJS workbook. */
export async function loadSdmsWorkbook(buf: ArrayBuffer): Promise<Workbook> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb;
}

/** Serialise `wb` exactly as each step used to: ExcelJS write, then restore the conditional-formatting
 *  `text=` attributes ExcelJS drops (sdmsCfTextRepair.ts — without them OG cannot open the blob). */
export async function writeSdmsWorkbook(wb: Workbook): Promise<ArrayBuffer> {
  normalizeCfNumFmts(wb);
  return repairCfTextAttrs((await wb.xlsx.writeBuffer()) as ArrayBuffer);
}

/**
 * ExcelJS writes a conditional-format style's number format as the format CODE string, but a dxf `<numFmt>` parsed
 * by an unpatched reader is the object `{id, formatCode}` — which the writer emits as `formatCode="[object Object]"`,
 * and DevExpress then refuses the whole workbook (ALPACA opens the batch as an empty Sheet1; QB26101301_001,
 * 2026-10-13). patches/exceljs+4.4.0.patch fixes the reader; this makes every write safe regardless of how the rule
 * got its style. Call it before any `wb.xlsx.writeBuffer()` of a workbook that is stored. Returns how many it fixed.
 */
export function normalizeCfNumFmts(wb: Workbook): number {
  let fixed = 0;
  for (const ws of wb.worksheets) {
    const cfs = (ws as unknown as { conditionalFormattings?: Array<{ rules?: Array<{ style?: Record<string, unknown> }> }> })
      .conditionalFormattings ?? [];
    for (const cf of cfs) for (const rule of cf.rules ?? []) {
      const nf = rule.style?.numFmt;
      if (nf === undefined || nf === null || typeof nf === 'string') continue;
      const code = (nf as { formatCode?: unknown }).formatCode;
      if (typeof code === 'string' && code) rule.style!.numFmt = code;
      else delete rule.style!.numFmt;
      fixed++;
    }
  }
  return fixed;
}

/** What ExcelJS's reader does to a sheet's print setup when it re-reads a written workbook (PageSetupXform
 *  parseOpen): a <pageSetup> element — written only when some attribute is set — comes back with every
 *  default filled in, and the writer then emits them (first page number, copies…). A sheet the old chain
 *  re-read carried those; one it did not (nothing wrote after it was added) did not. */
export function settleAddedSheet(ws: import('exceljs').Worksheet): void {
  const ps = ws.pageSetup as unknown as Record<string, unknown>;
  if (!ps) return;
  const renders = [
    ps.paperSize, ps.orientation, ps.horizontalDpi, ps.verticalDpi,
    ps.pageOrder === 'overThenDown' ? 1 : undefined, ps.blackAndWhite ? 1 : undefined, ps.draft ? 1 : undefined,
    ps.cellComments === 'atEnd' || ps.cellComments === 'asDisplyed' ? 1 : undefined,
    ps.errors === 'dash' || ps.errors === 'blank' || ps.errors === 'NA' ? 1 : undefined,
    ps.scale, ps.fitToWidth, ps.fitToHeight, ps.firstPageNumber, ps.usePrinterDefaults ? 1 : undefined, ps.copies,
  ].some(v => v !== undefined);
  if (!renders) return;
  const dflt = (k: string, v: unknown) => { if (ps[k] === undefined || ps[k] === null || ps[k] === '') ps[k] = v; };
  dflt('orientation', 'portrait');
  dflt('horizontalDpi', 4294967295);
  dflt('verticalDpi', 4294967295);
  dflt('pageOrder', 'downThenOver');
  dflt('cellComments', 'None');
  dflt('errors', 'displayed');
  dflt('scale', 100);
  dflt('fitToWidth', 1);
  dflt('fitToHeight', 1);
  dflt('firstPageNumber', 1);
  dflt('copies', 1);
  ps.blackAndWhite = !!ps.blackAndWhite;
  ps.draft = !!ps.draft;
  ps.usePrinterDefaults = !!ps.usePrinterDefaults;
}
